import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { apiFetch } from '@/lib/api';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkspace, useWorkspaceId, usePermissions } from '@/contexts/WorkspaceContext';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { CountrySelect } from '@/components/CountrySelect';
import { countryName } from '@/lib/countries';
import {
  LeadStatus,
  LEAD_STATUS_LABELS,
  MANUAL_LEAD_STATUSES,
  LEAD_STATUS_NONE,
  NOT_CONTACTED_LABEL,
  leadStatusLabel,
  LeadCampaign,
  LEAD_CAMPAIGN_LABELS,
  BUDGET_CURRENCIES,
  BudgetCurrency,
  formatBudget,
} from '@/lib/contactFields';
import { toast } from 'sonner';
import { Search, Plus, MoreHorizontal, Pencil, Trash2, Users, Loader2, BellOff, Upload } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MessageStatus,
  messageStatusVariant,
  messageStatusClass,
  messageStatusLabel,
  failureReason,
} from '@/lib/messageStatus';

interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  propertyInterest: string | null;
  budget: string | null;
  budgetCurrency: BudgetCurrency;
  locationPreference: string | null;
  currentCountry: string | null;
  notes: string | null;
  source: string;
  // null until a campaign reaches them — set by campaign results, never typed in.
  leadStatus: LeadStatus | null;
  leadCampaign: LeadCampaign;
  isUnsubscribed: boolean;
  // Opted out platform-wide (computed by the list endpoint, not stored on the contact). Only a
  // platform super admin may re-subscribe such a number, and doing so lifts it in every workspace.
  globallyOptedOut?: boolean;
  unsubscribedAt: string | null;
  lastContactedAt: string | null;
  messagesSentCount: number;
  // Denormalized messaging state — see backend/src/models/Contact.js.
  lastMessageStatus: MessageStatus | null;
  lastMessageStatusAt: string | null;
  lastFailureCode: string | null;
  lastReplyAt: string | null;
  lastReplyText: string | null;
  repliesCount: number;
  // 'global': this workspace never saw the request — the number opted out elsewhere on the platform.
  unsubscribeSource: 'manual' | 'inbound_optout' | 'import' | 'global' | null;
  createdAt: string;
}

interface ContactFormData {
  name: string;
  phone: string;
  email: string;
  propertyInterest: string;
  budget: string;
  budgetCurrency: BudgetCurrency;
  locationPreference: string;
  // '' for no country; the API stores it as null.
  currentCountry: string;
  notes: string;
  // Never sent on create; on edit, only once the contact has replied (see handleEditContact).
  leadStatus: LeadStatus | null;
  leadCampaign: LeadCampaign;
  isUnsubscribed: boolean;
}

const emptyFormData: ContactFormData = {
  name: '',
  phone: '',
  email: '',
  propertyInterest: '',
  budget: '',
  budgetCurrency: 'AED',
  locationPreference: '',
  currentCountry: '',
  notes: '',
  leadStatus: null,
  leadCampaign: 'current',
  isUnsubscribed: false,
};

// Turning consent on or off for a number is platform-wide, so the edit form asks first.
type ConsentChange = 'opt_out' | 'lift';

// Lead status is a campaign result. A person may correct it only once there is a reply to judge —
// the same rule backend/src/controllers/contact/updateContact.js enforces.
const canEditLeadStatus = (contact: Contact | null) => Boolean(contact && contact.repliesCount > 0);

export default function Contacts() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');
  const [leadCampaignFilter, setLeadCampaignFilter] = useState('all');
  // '' = every country.
  const [countryFilter, setCountryFilter] = useState('');
  const [hideUnsubscribed, setHideUnsubscribed] = useState(false);
  const [messageStatusFilter, setMessageStatusFilter] = useState('all');
  const [repliedFilter, setRepliedFilter] = useState('any');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<ContactFormData>(emptyFormData);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [consentConfirm, setConsentConfirm] = useState<ConsentChange | null>(null);

  const workspaceId = useWorkspaceId();
  const { isSuperAdmin } = useWorkspace();
  const { can } = usePermissions();
  const { i18n } = useTranslation();
  const { guard, nudge } = useSubscriptionGuard('Managing contacts requires an active subscription.');

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (leadStatusFilter !== 'all') params.append('leadStatus', leadStatusFilter);
      if (leadCampaignFilter !== 'all') params.append('leadCampaign', leadCampaignFilter);
      if (countryFilter) params.append('currentCountry', countryFilter);
      if (hideUnsubscribed) params.append('isUnsubscribed', 'false');
      if (messageStatusFilter !== 'all') params.append('messageStatus', messageStatusFilter);
      if (repliedFilter !== 'any') params.append('replied', repliedFilter === 'replied' ? 'true' : 'false');
      params.append('page', page.toString());
      params.append('limit', '20');

      const data = await apiFetch<{
        contacts: Contact[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      }>(`/workspaces/${workspaceId}/contacts?${params.toString()}`);

      setContacts(data.contacts);
      setTotalPages(data.pagination.totalPages);
      setTotal(data.pagination.total);
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [workspaceId, page, sourceFilter, leadStatusFilter, leadCampaignFilter, countryFilter, hideUnsubscribed, messageStatusFilter, repliedFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(1);
      fetchContacts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const handleAddContact = async () => {
    setSubmitting(true);
    try {
      // Lead status comes from campaign results; the API refuses one on create.
      const { leadStatus: _leadStatus, ...fields } = formData;
      const data = await apiFetch<{ contact: Contact; globallyOptedOut?: boolean }>(`/workspaces/${workspaceId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(fields),
      });
      if (data?.globallyOptedOut) {
        // Said out loud, or the new contact silently never receives anything and nobody knows why.
        toast.info('Contact added as unsubscribed: this number has opted out of all messages on the platform.');
      } else {
        toast.success('Contact added successfully');
      }
      setIsAddDialogOpen(false);
      setFormData(emptyFormData);
      fetchContacts();
    } catch (error) {
      // A duplicate phone number (409) or an unreadable one (400) comes back with a message that
      // says which contact already has it — far more use than 'Failed to add contact'.
      toast.error(error instanceof Error ? error.message : 'Failed to add contact');
    } finally {
      setSubmitting(false);
    }
  };

  // An opt-out belongs to the NUMBER, platform-wide, so changing consent on a contact with a phone
  // changes it for every workspace that holds the number. That is asked about first, not discovered.
  const requestEditSave = () => {
    if (!selectedContact) return;
    const changed = formData.isUnsubscribed !== selectedContact.isUnsubscribed;
    if (changed && formData.isUnsubscribed && formData.phone.trim()) {
      setConsentConfirm('opt_out');
    } else if (changed && !formData.isUnsubscribed && selectedContact.globallyOptedOut) {
      setConsentConfirm('lift');
    } else {
      handleEditContact();
    }
  };

  const handleEditContact = async () => {
    if (!selectedContact) return;

    setConsentConfirm(null);
    setSubmitting(true);
    try {
      // Lead status only when it was actually changed — which the form allows only once the contact
      // has replied. The API refuses it otherwise, so sending the unchanged value would fail the save.
      const { leadStatus, ...fields } = formData;
      const leadStatusChanged = canEditLeadStatus(selectedContact) && leadStatus !== selectedContact.leadStatus;
      await apiFetch(`/workspaces/${workspaceId}/contacts/${selectedContact.id}`, {
        method: 'PATCH',
        body: JSON.stringify(leadStatusChanged ? { ...fields, leadStatus } : fields),
      });
      toast.success('Contact updated successfully');
      setIsEditDialogOpen(false);
      setSelectedContact(null);
      setFormData(emptyFormData);
      fetchContacts();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update contact');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;

    setSubmitting(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/contacts/${selectedContact.id}`, {
        method: 'DELETE',
      });
      toast.success('Contact deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedContact(null);
      fetchContacts();
    } catch {
      toast.error('Failed to delete contact');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setBulkDeleting(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/contacts/bulk`, {
        method: 'DELETE',
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      toast.success(`${selectedIds.size} contact(s) deleted`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      fetchContacts();
    } catch {
      toast.error('Failed to delete contacts');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEditDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name || '',
      phone: contact.phone || '',
      email: contact.email || '',
      propertyInterest: contact.propertyInterest || '',
      budget: contact.budget || '',
      budgetCurrency: contact.budgetCurrency || 'AED',
      locationPreference: contact.locationPreference || '',
      currentCountry: contact.currentCountry || '',
      notes: contact.notes || '',
      leadStatus: contact.leadStatus ?? null,
      leadCampaign: contact.leadCampaign || 'current',
      isUnsubscribed: Boolean(contact.isUnsubscribed),
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setIsDeleteDialogOpen(true);
  };

  const getSourceBadgeVariant = (source: string) => {
    switch (source) {
      case 'manual':
        return 'secondary';
      case 'excel_import':
      case 'csv_import':
        return 'default';
      default:
        return 'outline';
    }
  };

  const getLeadStatusBadgeVariant = (status: LeadStatus | null) => {
    switch (status) {
      case 'interested':
        return 'default';
      case 'not_interested':
        return 'destructive';
      case 'replied':
      case 'contacted':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // `showConsent` is false in the Add dialog: a contact you are typing in has not had the chance
  // to unsubscribe yet, and offering the toggle there invites creating one who can never be
  // messaged without anyone noticing why. Lead status is hidden there for the same kind of reason:
  // no campaign has produced a result for them yet.
  const renderContactForm = (onSubmit: () => void, submitLabel: string, showConsent = false) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="John Doe"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+971501234567"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@example.com"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="propertyInterest">Property Interest</Label>
          <Input
            id="propertyInterest"
            value={formData.propertyInterest}
            onChange={(e) => setFormData({ ...formData, propertyInterest: e.target.value })}
            placeholder="Apartment, Villa, etc."
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="budget">Budget</Label>
          <div className="flex gap-2">
            <Input
              id="budget"
              value={formData.budget}
              onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              placeholder="1,500,000"
              className="min-w-0"
            />
            <Select
              value={formData.budgetCurrency}
              onValueChange={(value) => setFormData({ ...formData, budgetCurrency: value as BudgetCurrency })}
            >
              <SelectTrigger className="w-[88px] shrink-0" aria-label="Budget currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUDGET_CURRENCIES.map((currency) => (
                  <SelectItem key={currency} value={currency}>
                    {currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="locationPreference">Location Preference</Label>
          <Input
            id="locationPreference"
            value={formData.locationPreference}
            onChange={(e) => setFormData({ ...formData, locationPreference: e.target.value })}
            placeholder="Dubai Marina, Downtown, etc."
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="currentCountry">Current Country</Label>
          <CountrySelect
            id="currentCountry"
            value={formData.currentCountry}
            onChange={(code) => setFormData({ ...formData, currentCountry: code })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes about the contact..."
          rows={3}
        />
      </div>
      <div className={showConsent ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
        {/* Not on Add at all: a contact being typed in has no campaign results yet. */}
        {showConsent && (
          <div className="space-y-2">
            <Label htmlFor="leadStatus">Lead Status</Label>
            {canEditLeadStatus(selectedContact) ? (
              <Select
                value={formData.leadStatus ?? undefined}
                onValueChange={(value) => setFormData({ ...formData, leadStatus: value as LeadStatus })}
              >
                <SelectTrigger id="leadStatus">
                  <SelectValue placeholder={leadStatusLabel(formData.leadStatus)} />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_LEAD_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {LEAD_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="space-y-1">
                <Badge variant={getLeadStatusBadgeVariant(formData.leadStatus)}>
                  {leadStatusLabel(formData.leadStatus)}
                </Badge>
                <p className="text-xs text-muted-foreground">
                  Set automatically from campaign results. You can change it once the contact replies.
                </p>
              </div>
            )}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="leadCampaign">Lead Campaign</Label>
          <Select
            value={formData.leadCampaign}
            onValueChange={(value) => setFormData({ ...formData, leadCampaign: value as LeadCampaign })}
          >
            <SelectTrigger id="leadCampaign">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(LEAD_CAMPAIGN_LABELS) as LeadCampaign[]).map((campaign) => (
                <SelectItem key={campaign} value={campaign}>
                  {LEAD_CAMPAIGN_LABELS[campaign]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {showConsent && (() => {
        // Locked for everyone but a platform super admin once the NUMBER has opted out: re-subscribing
        // it would undo the opt-out in every other workspace holding it too. The API refuses it
        // either way; this just says so before anyone tries.
        const locked = Boolean(selectedContact?.globallyOptedOut && selectedContact.isUnsubscribed && !isSuperAdmin);
        return (
          <div className="flex items-start justify-between rounded-md border p-3">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor="isUnsubscribed">Unsubscribed</Label>
              <p className="text-sm text-muted-foreground">
                {locked
                  ? 'This number has opted out of all messages on the platform. Only platform support can re-subscribe it.'
                  : selectedContact?.globallyOptedOut && isSuperAdmin
                    ? 'Opted out platform-wide. Turning this off re-subscribes the number in every workspace.'
                    : 'Excluded from every campaign, including when picked by hand. Opting out a phone number applies in every workspace.'}
              </p>
            </div>
            <Switch
              id="isUnsubscribed"
              checked={formData.isUnsubscribed}
              disabled={locked}
              onCheckedChange={(checked) => setFormData({ ...formData, isUnsubscribed: checked })}
            />
          </div>
        );
      })()}
      <div className="flex justify-end gap-2 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setIsAddDialogOpen(false);
            setIsEditDialogOpen(false);
            setFormData(emptyFormData);
          }}
        >
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <CardTitle>Contacts</CardTitle>
              <Badge variant="outline" className="ml-2">
                {total} total
              </Badge>
            </div>
            <div className="flex gap-2">
              {can('uploads:create') && (
                <Button variant="outline" asChild>
                  <Link to={`/w/${workspaceId}/upload`}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Contacts
                  </Link>
                </Button>
              )}
              <Button onClick={guard(() => setIsAddDialogOpen(true))}>
                <Plus className="mr-2 h-4 w-4" />
                Add Contact
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="excel_import">Excel Import</SelectItem>
                <SelectItem value="csv_import">CSV Import</SelectItem>
              </SelectContent>
            </Select>
            <Select value={leadStatusFilter} onValueChange={setLeadStatusFilter}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value={LEAD_STATUS_NONE}>{NOT_CONTACTED_LABEL}</SelectItem>
                {(Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((status) => (
                  <SelectItem key={status} value={status}>
                    {LEAD_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={leadCampaignFilter}
              onValueChange={(value) => { setPage(1); setLeadCampaignFilter(value); }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Lead campaign" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lead Campaigns</SelectItem>
                {(Object.keys(LEAD_CAMPAIGN_LABELS) as LeadCampaign[]).map((campaign) => (
                  <SelectItem key={campaign} value={campaign}>
                    {LEAD_CAMPAIGN_LABELS[campaign]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <CountrySelect
              value={countryFilter}
              onChange={(code) => { setPage(1); setCountryFilter(code); }}
              emptyLabel="All Countries"
              className="w-[180px]"
            />
            <Select
              value={messageStatusFilter}
              onValueChange={(value) => { setPage(1); setMessageStatusFilter(value); }}
            >
              <SelectTrigger className="w-[185px]">
                <SelectValue placeholder="Message status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any message status</SelectItem>
                <SelectItem value="none">Never messaged</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="capped">Daily cap</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={repliedFilter}
              onValueChange={(value) => { setPage(1); setRepliedFilter(value); }}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Replies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any reply</SelectItem>
                <SelectItem value="replied">Replied</SelectItem>
                <SelectItem value="none">No reply</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Switch
                id="hideUnsubscribed"
                checked={hideUnsubscribed}
                onCheckedChange={(checked) => {
                  setPage(1);
                  setHideUnsubscribed(checked);
                }}
              />
              <Label htmlFor="hideUnsubscribed" className="text-sm font-normal">
                Hide unsubscribed
              </Label>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No contacts found</p>
              <p className="text-sm">
                {searchTerm || sourceFilter !== 'all' || leadStatusFilter !== 'all' || leadCampaignFilter !== 'all'
                  || countryFilter || hideUnsubscribed
                  ? 'Try adjusting your search or filters'
                  : 'Upload a file or add contacts manually to get started'}
              </p>
            </div>
          ) : (
            <>
              {selectedIds.size > 0 && (
                <div className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-2 mb-4">
                  <span className="text-sm font-medium">
                    {selectedIds.size} contact{selectedIds.size > 1 ? 's' : ''} selected
                  </span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                      Clear
                    </Button>
                    <Button variant="destructive" size="sm" onClick={guard(() => setBulkDeleteOpen(true))}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete Selected
                    </Button>
                  </div>
                </div>
              )}

              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={contacts.length > 0 && selectedIds.size === contacts.length}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Lead Campaign</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Property Interest</TableHead>
                      <TableHead>Budget</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Last Contacted</TableHead>
                      <TableHead>Delivery Status</TableHead>
                      <TableHead>Latest Reply</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow key={contact.id} className={selectedIds.has(contact.id) ? 'bg-muted/40' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(contact.id)}
                            onCheckedChange={() => toggleSelect(contact.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {contact.name || '-'}
                        </TableCell>
                        <TableCell>
                          {contact.isUnsubscribed ? (
                            // Shown instead of the funnel status, not beside it: for anyone
                            // scanning the list, "will not be messaged" is the fact that matters.
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="destructive" className="gap-1">
                                  <BellOff className="h-3 w-3" />
                                  Unsubscribed
                                </Badge>
                              </TooltipTrigger>
                              {/* Opt-out can now happen without a human involved, so the list has
                                  to be able to say why — otherwise the first support question about
                                  it has no answer. */}
                              {/* 'global' never names the workspace the request was made to: that a
                                  number opted out is unavoidable to show, who it was talking to is not. */}
                              <TooltipContent className="max-w-xs">
                                <p>
                                  {contact.unsubscribeSource === 'inbound_optout'
                                    ? `Replied asking to stop${contact.unsubscribedAt ? ` on ${new Date(contact.unsubscribedAt).toLocaleDateString()}` : ''}`
                                    : contact.unsubscribeSource === 'global'
                                      ? 'This number opted out of all messages on the platform'
                                      : 'Unsubscribed manually'}
                                </p>
                                {contact.globallyOptedOut && (
                                  <p className="text-muted-foreground">
                                    Blocked in every workspace. Only platform support can re-subscribe it.
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Badge variant={getLeadStatusBadgeVariant(contact.leadStatus)}>
                              {leadStatusLabel(contact.leadStatus)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={contact.leadCampaign === 'current' ? 'secondary' : 'outline'} className="whitespace-nowrap">
                            {LEAD_CAMPAIGN_LABELS[contact.leadCampaign] || contact.leadCampaign}
                          </Badge>
                        </TableCell>
                        <TableCell>{contact.phone || '-'}</TableCell>
                        <TableCell>{contact.email || '-'}</TableCell>
                        <TableCell>{contact.propertyInterest || '-'}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatBudget(contact.budget, contact.budgetCurrency) || '-'}
                        </TableCell>
                        <TableCell>{contact.locationPreference || '-'}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {contact.currentCountry ? countryName(contact.currentCountry, i18n.language) : '-'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {contact.lastContactedAt
                            ? new Date(contact.lastContactedAt).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell>
                          {contact.lastMessageStatus ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant={messageStatusVariant(contact.lastMessageStatus, contact.lastFailureCode)}
                                  className={messageStatusClass(contact.lastMessageStatus, contact.lastFailureCode)}
                                >
                                  {messageStatusLabel(contact.lastMessageStatus, contact.lastFailureCode)}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                {/* The reason is what makes a failure actionable: "daily cap" means
                                    try again tomorrow, "not on WhatsApp" means remove the row. */}
                                {contact.lastMessageStatus === 'failed'
                                  ? failureReason(contact.lastFailureCode)?.hint
                                  : `Last message ${messageStatusLabel(contact.lastMessageStatus).toLowerCase()}${contact.lastMessageStatusAt ? ` on ${new Date(contact.lastMessageStatusAt).toLocaleDateString()}` : ''}`}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[220px]">
                          {contact.lastReplyAt ? (
                            <div>
                              {/* dir="auto" because most replies here are Arabic while the layout
                                  is LTR unless the UI language is switched. */}
                              <p className="text-sm line-clamp-1" dir="auto">
                                {contact.lastReplyText || '(no text)'}
                              </p>
                              <span className="text-xs text-muted-foreground">
                                {new Date(contact.lastReplyAt).toLocaleDateString()}
                                {contact.repliesCount > 1 ? ` · ${contact.repliesCount} replies` : ''}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getSourceBadgeVariant(contact.source)}>
                            {contact.source === 'excel_import' ? 'Excel' : contact.source === 'csv_import' ? 'CSV' : contact.source}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={guard(() => openEditDialog(contact))}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={guard(() => openDeleteDialog(contact))}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={() => setPage(pageNum)}
                              isActive={page === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Contact Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
          </DialogHeader>
          {renderContactForm(handleAddContact, "Add Contact")}
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          {renderContactForm(requestEditSave, "Save Changes", true)}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-medium text-foreground">
              {selectedContact?.name || selectedContact?.email || selectedContact?.phone || 'this contact'}
            </span>
            ? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteContact} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Platform-wide consent change. A misclick here blocks — or unblocks — a real person in every
          workspace, which is why it is a confirmation rather than a toggle that just saves. */}
      <AlertDialog open={consentConfirm !== null} onOpenChange={(open) => { if (!open) setConsentConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {consentConfirm === 'lift' ? 'Re-subscribe this number everywhere?' : 'Opt this number out everywhere?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {consentConfirm === 'lift'
                ? `${formData.phone || 'This number'} opted out of all messages. Re-subscribing it lifts the opt-out in every workspace that holds this number — only do this if the person asked to hear from us again.`
                : `${formData.phone || 'This number'} will stop receiving messages from every workspace on the platform, not just this one. Only platform support can undo this.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={consentConfirm === 'opt_out' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
              onClick={handleEditContact}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {consentConfirm === 'lift' ? 'Re-subscribe everywhere' : 'Opt out everywhere'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} contact{selectedIds.size > 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected contact{selectedIds.size > 1 ? 's' : ''}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
            >
              {bulkDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {nudge}
      </div>
    </div>
  );
}
